import type {
  NodeReleaseRecord,
  NodeResourceMetrics,
  NodeResourceState,
  OctreeGeometry,
  OctreeGeometryNode,
} from '@/plugins/pointCloud/core/OctreeGeometry'
import type {
  NodeLoadSettledEvent,
  NodeLoadStartedEvent,
} from '@/plugins/pointCloud/loaders/OctreeLoader'
import { ResidentNodeLRU } from '@/plugins/pointCloud/ResidentNodeLRU'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { AssetId } from '@/shared/types/core'
import { BinaryHeap } from '@/shared/utils/BinaryHeap'

const log = getLoggerManager().getLogger('pointcloud:memory')
const TRIM_DIAGNOSTIC_INTERVAL_MS = 15000
const DEFAULT_MAX_CACHED_POINTS = 1_000_000

/** 驻留预算配置。字节指标未配置时仅用于诊断。 */
export interface PointCloudResidencyBudgetOptions {
  /** 总驻留点数硬上限；未配置时不启用总点数限制。 */
  maxResidentPoints?: number
  /** 非当前工作集的冷缓存点数上限，默认 1_000_000。 */
  maxCachedPoints?: number
  maxEstimatedResidentBytes?: number
  lowWatermarkRatio?: number
}

export type PointCloudTrimTrigger = 'none' | 'points' | 'estimatedBytes' | 'both'

/** 最近一次预算维护的真实结果。 */
export interface PointCloudTrimSummary {
  trigger: PointCloudTrimTrigger
  selected: number
  removed: number
  releasedPoints: number
  releasedEstimatedCpuBytes: number
  releasedEstimatedGpuBytes: number
  releasedEstimatedTotalBytes: number
  blockedByProtection: number
}

/** 常数级、只读的驻留诊断快照。 */
export interface PointCloudResidencySnapshot {
  residentEntries: number
  residentPoints: number
  estimatedResidentCpuBytes: number
  estimatedResidentGpuBytes: number
  estimatedResidentTotalBytes: number
  protectedEntries: number
  protectedResidentPoints: number
  cachedResidentPoints: number
  pointHighWatermark: number | null
  pointLowWatermark: number | null
  cachedPointHighWatermark: number
  cachedPointLowWatermark: number
  estimatedByteHighWatermark: number | null
  estimatedByteLowWatermark: number | null
  trimTrigger: PointCloudTrimTrigger
  trimCount: number
  trimRemovedTotal: number
  trimReleasedPointsTotal: number
  trimReleasedEstimatedBytesTotal: number
  trimBlockedByProtectionTotal: number
  assetUnloadCount: number
  assetUnloadRemovedTotal: number
  assetUnloadReleasedPointsTotal: number
  assetUnloadReleasedEstimatedBytesTotal: number
  loadStartedCount: number
  initialLoadStartedCount: number
  retryStartedCount: number
  reloadStartedCount: number
  loadCompletedCount: number
  structuralCompletedCount: number
  loadFailedCount: number
  loadAbortedCount: number
  lateResultDroppedCount: number
  inFlightLoads: number
  peakInFlightLoads: number
  peakResidentEntries: number
  peakResidentPoints: number
  peakEstimatedResidentCpuBytes: number
  peakEstimatedResidentGpuBytes: number
  peakEstimatedResidentTotalBytes: number
  peakProtectedEntries: number
  lastTrim: PointCloudTrimSummary
}

interface ResidentEntry extends NodeResourceMetrics {
  assetId: AssetId
  state: Extract<NodeResourceState, 'decoded' | 'resident'>
}

interface RankedCandidate {
  node: OctreeGeometryNode
  rank: number
}

const EMPTY_TRIM_SUMMARY: PointCloudTrimSummary = {
  trigger: 'none',
  selected: 0,
  removed: 0,
  releasedPoints: 0,
  releasedEstimatedCpuBytes: 0,
  releasedEstimatedGpuBytes: 0,
  releasedEstimatedTotalBytes: 0,
  blockedByProtection: 0,
}

function assertPositiveFinite(name: string, value: number): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} 必须是大于 0 的有限数值`)
  }
}

/** 管理点云驻留登记、计量、保护集合与预算淘汰。 */
export class PointCloudResidencyManager {
  private readonly lru = new ResidentNodeLRU()
  private readonly entries = new Map<OctreeGeometryNode, ResidentEntry>()
  private readonly residentDescendantCounts = new Map<OctreeGeometryNode, number>()
  private readonly geometryAssets = new Map<OctreeGeometry, AssetId>()
  private readonly assetGeometries = new Map<AssetId, OctreeGeometry>()
  private readonly assetListenerDisposers = new Map<AssetId, () => void>()
  private protectedNodes = new Set<OctreeGeometryNode>()

  private residentPoints = 0
  private estimatedResidentCpuBytes = 0
  private estimatedResidentGpuBytes = 0
  private estimatedResidentTotalBytes = 0
  private protectedEntries = 0
  private protectedResidentPoints = 0
  private trimCount = 0
  private trimRemovedTotal = 0
  private trimReleasedPointsTotal = 0
  private trimReleasedEstimatedBytesTotal = 0
  private trimBlockedByProtectionTotal = 0
  private assetUnloadCount = 0
  private assetUnloadRemovedTotal = 0
  private assetUnloadReleasedPointsTotal = 0
  private assetUnloadReleasedEstimatedBytesTotal = 0
  private loadStartedCount = 0
  private initialLoadStartedCount = 0
  private retryStartedCount = 0
  private reloadStartedCount = 0
  private loadCompletedCount = 0
  private structuralCompletedCount = 0
  private loadFailedCount = 0
  private loadAbortedCount = 0
  private lateResultDroppedCount = 0
  private inFlightLoads = 0
  private peakInFlightLoads = 0
  private peakResidentEntries = 0
  private peakResidentPoints = 0
  private peakEstimatedResidentCpuBytes = 0
  private peakEstimatedResidentGpuBytes = 0
  private peakEstimatedResidentTotalBytes = 0
  private peakProtectedEntries = 0
  private lastTrimDiagnosticAt = Number.NEGATIVE_INFINITY
  private lastTrim: PointCloudTrimSummary = { ...EMPTY_TRIM_SUMMARY }

  readonly pointHighWatermark: number | null
  readonly pointLowWatermark: number | null
  readonly cachedPointHighWatermark: number
  readonly cachedPointLowWatermark: number
  readonly estimatedByteHighWatermark: number | null
  readonly estimatedByteLowWatermark: number | null

  constructor(options: PointCloudResidencyBudgetOptions) {
    const lowWatermarkRatio = options.lowWatermarkRatio ?? 0.85
    if (!Number.isFinite(lowWatermarkRatio) || lowWatermarkRatio <= 0 || lowWatermarkRatio >= 1) {
      throw new Error('lowWatermarkRatio 必须大于 0 且小于 1')
    }
    if (options.maxResidentPoints !== undefined) {
      assertPositiveFinite('maxResidentPoints', options.maxResidentPoints)
    }
    const maxCachedPoints = options.maxCachedPoints ?? DEFAULT_MAX_CACHED_POINTS
    assertPositiveFinite('maxCachedPoints', maxCachedPoints)
    if (options.maxEstimatedResidentBytes !== undefined) {
      assertPositiveFinite('maxEstimatedResidentBytes', options.maxEstimatedResidentBytes)
    }

    this.pointHighWatermark = options.maxResidentPoints ?? null
    this.pointLowWatermark =
      options.maxResidentPoints === undefined
        ? null
        : Math.floor(options.maxResidentPoints * lowWatermarkRatio)
    this.cachedPointHighWatermark = maxCachedPoints
    this.cachedPointLowWatermark = Math.floor(maxCachedPoints * lowWatermarkRatio)
    this.estimatedByteHighWatermark = options.maxEstimatedResidentBytes ?? null
    this.estimatedByteLowWatermark =
      options.maxEstimatedResidentBytes === undefined
        ? null
        : Math.floor(options.maxEstimatedResidentBytes * lowWatermarkRatio)
  }

  /** 将资产拓扑接入驻留状态事实源。 */
  attachAsset(assetId: AssetId, geometry: OctreeGeometry): void {
    this.detachAsset(assetId)
    this.geometryAssets.set(geometry, assetId)
    this.assetGeometries.set(assetId, geometry)
    const disposeListener =
      typeof geometry.addResourceStateListener === 'function'
        ? geometry.addResourceStateListener((node) => this.syncNode(node))
        : () => {}
    this.assetListenerDisposers.set(assetId, disposeListener)

    if (!geometry.root) {
      return
    }
    this.forEachTopologyNode(geometry.root, (node) => {
      this.syncNode(node)
    })
  }

  /** 断开资产登记；调用方应先 unload 或 dispose 其载荷。 */
  detachAsset(assetId: AssetId): void {
    this.assetListenerDisposers.get(assetId)?.()
    this.assetListenerDisposers.delete(assetId)
    const geometry = this.assetGeometries.get(assetId)
    if (geometry) {
      this.geometryAssets.delete(geometry)
      this.assetGeometries.delete(assetId)
    }

    for (const [node, entry] of this.entries) {
      if (entry.assetId === assetId) {
        this.removeEntry(node, entry)
      }
    }
    this.recountProtectedEntries()
  }

  /** 可见性或渲染路径只更新已登记节点的最近使用顺序。 */
  touch(node: OctreeGeometryNode): void {
    if (!this.entries.has(node)) {
      this.syncNode(node)
    }
    if (this.entries.has(node)) {
      this.lru.touch(node)
    }
  }

  /** 累计节点加载开始事件，不打印逐节点正常日志。 */
  recordNodeLoadStarted(event: NodeLoadStartedEvent): void {
    this.loadStartedCount += 1
    this.inFlightLoads += 1
    if (event.kind === 'initial') {
      this.initialLoadStartedCount += 1
    } else if (event.kind === 'retry') {
      this.retryStartedCount += 1
    } else {
      this.reloadStartedCount += 1
    }
    this.peakInFlightLoads = Math.max(this.peakInFlightLoads, this.inFlightLoads)
  }

  /** 累计节点加载终态；late-dropped 表示结果实际到达但被 token 拒绝。 */
  recordNodeLoadSettled(event: NodeLoadSettledEvent): void {
    this.inFlightLoads = Math.max(0, this.inFlightLoads - 1)
    if (event.status === 'loaded') {
      this.loadCompletedCount += 1
    } else if (event.status === 'structural') {
      this.structuralCompletedCount += 1
    } else if (event.status === 'failed') {
      this.loadFailedCount += 1
    } else if (event.status === 'aborted') {
      this.loadAbortedCount += 1
    } else {
      this.lateResultDroppedCount += 1
    }
  }

  /** 设置本帧身份型保护集合，并自动加入必要祖先闭包。 */
  setProtectedNodes(nodes: ReadonlySet<OctreeGeometryNode>): void {
    const protectedNodes = new Set<OctreeGeometryNode>()
    for (const node of nodes) {
      let current: OctreeGeometryNode | null | undefined = node
      while (current) {
        protectedNodes.add(current)
        current = current.parent
      }
    }
    this.protectedNodes = protectedNodes
    this.recountProtectedEntries()
  }

  /** 显式卸载资产载荷，保留 Loader 与拓扑以支持后续重载。 */
  unloadAsset(assetId: AssetId): PointCloudTrimSummary {
    const summary = this.releaseAssetPayload(assetId)
    if (this.assetGeometries.has(assetId)) {
      this.assetUnloadCount += 1
      this.assetUnloadRemovedTotal += summary.removed
      this.assetUnloadReleasedPointsTotal += summary.releasedPoints
      this.assetUnloadReleasedEstimatedBytesTotal += summary.releasedEstimatedTotalBytes
    }
    return summary
  }

  private releaseAssetPayload(assetId: AssetId): PointCloudTrimSummary {
    const geometry = this.assetGeometries.get(assetId)
    const summary = { ...EMPTY_TRIM_SUMMARY }
    if (!geometry?.root) {
      return summary
    }

    this.forEachTopologyNode(geometry.root, (node) => {
      const release = node.unload()
      if (release.released) {
        this.accumulateRelease(summary, release)
        summary.removed += 1
      }
    })
    return summary
  }

  /** 在任一高水位超限后逐节点回收到全部已配置指标的低水位。 */
  maintainBudget(): PointCloudTrimSummary {
    const trigger = this.getTrimTrigger()
    if (trigger === 'none') {
      this.lastTrim = { ...EMPTY_TRIM_SUMMARY }
      return { ...this.lastTrim }
    }

    const beforeEntries = this.entries.size
    const beforePoints = this.residentPoints
    const beforeCachedPoints = this.getCachedResidentPoints()
    const beforeEstimatedBytes = this.estimatedResidentTotalBytes
    const summary: PointCloudTrimSummary = { ...EMPTY_TRIM_SUMMARY, trigger }
    const blockedNodes = new Set<OctreeGeometryNode>()
    const ranks = new Map<OctreeGeometryNode, number>()
    const queuedNodes = new Set<OctreeGeometryNode>()
    const candidates = new BinaryHeap<RankedCandidate>((candidate) => candidate.rank)
    const cursor = this.lru.createOldestFirstCursor()
    let rank = 0
    for (let node = cursor.next(); node; node = cursor.next()) {
      ranks.set(node, rank)
      rank += 1
      this.enqueueSafeCandidate(node, ranks, candidates, queuedNodes, blockedNodes)
    }

    while (this.isAboveLowWatermarks()) {
      const candidate = this.popSafeCandidate(candidates, queuedNodes, blockedNodes)
      if (!candidate) {
        break
      }

      summary.selected += 1
      const release = candidate.unload()
      if (this.entries.has(candidate)) {
        log.warn(
          `event=pointcloud.residency.trim phase=stalled node=${candidate.name} state=${candidate.resourceState}`
        )
        break
      }
      if (release.released) {
        summary.removed += 1
        this.accumulateRelease(summary, release)
      }
      this.enqueueSafeAncestors(candidate.parent, ranks, candidates, queuedNodes, blockedNodes)
    }
    summary.blockedByProtection = this.isAboveLowWatermarks() ? blockedNodes.size : 0

    this.trimCount += 1
    this.trimRemovedTotal += summary.removed
    this.trimReleasedPointsTotal += summary.releasedPoints
    this.trimReleasedEstimatedBytesTotal += summary.releasedEstimatedTotalBytes
    this.trimBlockedByProtectionTotal += summary.blockedByProtection
    this.lastTrim = summary
    if (log.isEnabled(LogLevel.DEBUG)) {
      const now = getLoggerManager().now()
      if (summary.removed > 0 || now - this.lastTrimDiagnosticAt >= TRIM_DIAGNOSTIC_INTERVAL_MS) {
        this.lastTrimDiagnosticAt = now
        log.debug(
          `event=pointcloud.residency.trim phase=complete trigger=${summary.trigger} selected=${summary.selected} removed=${summary.removed} releasedPoints=${summary.releasedPoints} releasedEstimatedCpuBytes=${summary.releasedEstimatedCpuBytes} releasedEstimatedGpuBytes=${summary.releasedEstimatedGpuBytes} releasedEstimatedTotalBytes=${summary.releasedEstimatedTotalBytes} blockedByProtection=${summary.blockedByProtection} beforeEntries=${beforeEntries} afterEntries=${this.entries.size} beforePoints=${beforePoints} afterPoints=${this.residentPoints} beforeCachedPoints=${beforeCachedPoints} afterCachedPoints=${this.getCachedResidentPoints()} beforeEstimatedTotalBytes=${beforeEstimatedBytes} afterEstimatedTotalBytes=${this.estimatedResidentTotalBytes} protectedEntries=${this.protectedEntries} protectedResidentPoints=${this.protectedResidentPoints} cachedPointHighWatermark=${this.cachedPointHighWatermark} cachedPointLowWatermark=${this.cachedPointLowWatermark} pointHighWatermark=${this.pointHighWatermark ?? 'disabled'} pointLowWatermark=${this.pointLowWatermark ?? 'disabled'} estimatedByteHighWatermark=${this.estimatedByteHighWatermark ?? 'disabled'} estimatedByteLowWatermark=${this.estimatedByteLowWatermark ?? 'disabled'}`
        )
      }
    }
    return { ...summary }
  }

  /** 获取不触碰 LRU、不遍历资产拓扑的只读诊断快照。 */
  getSnapshot(): PointCloudResidencySnapshot {
    return {
      residentEntries: this.entries.size,
      residentPoints: this.residentPoints,
      estimatedResidentCpuBytes: this.estimatedResidentCpuBytes,
      estimatedResidentGpuBytes: this.estimatedResidentGpuBytes,
      estimatedResidentTotalBytes: this.estimatedResidentTotalBytes,
      protectedEntries: this.protectedEntries,
      protectedResidentPoints: this.protectedResidentPoints,
      cachedResidentPoints: this.getCachedResidentPoints(),
      pointHighWatermark: this.pointHighWatermark,
      pointLowWatermark: this.pointLowWatermark,
      cachedPointHighWatermark: this.cachedPointHighWatermark,
      cachedPointLowWatermark: this.cachedPointLowWatermark,
      estimatedByteHighWatermark: this.estimatedByteHighWatermark,
      estimatedByteLowWatermark: this.estimatedByteLowWatermark,
      trimTrigger: this.getTrimTrigger(),
      trimCount: this.trimCount,
      trimRemovedTotal: this.trimRemovedTotal,
      trimReleasedPointsTotal: this.trimReleasedPointsTotal,
      trimReleasedEstimatedBytesTotal: this.trimReleasedEstimatedBytesTotal,
      trimBlockedByProtectionTotal: this.trimBlockedByProtectionTotal,
      assetUnloadCount: this.assetUnloadCount,
      assetUnloadRemovedTotal: this.assetUnloadRemovedTotal,
      assetUnloadReleasedPointsTotal: this.assetUnloadReleasedPointsTotal,
      assetUnloadReleasedEstimatedBytesTotal: this.assetUnloadReleasedEstimatedBytesTotal,
      loadStartedCount: this.loadStartedCount,
      initialLoadStartedCount: this.initialLoadStartedCount,
      retryStartedCount: this.retryStartedCount,
      reloadStartedCount: this.reloadStartedCount,
      loadCompletedCount: this.loadCompletedCount,
      structuralCompletedCount: this.structuralCompletedCount,
      loadFailedCount: this.loadFailedCount,
      loadAbortedCount: this.loadAbortedCount,
      lateResultDroppedCount: this.lateResultDroppedCount,
      inFlightLoads: this.inFlightLoads,
      peakInFlightLoads: this.peakInFlightLoads,
      peakResidentEntries: this.peakResidentEntries,
      peakResidentPoints: this.peakResidentPoints,
      peakEstimatedResidentCpuBytes: this.peakEstimatedResidentCpuBytes,
      peakEstimatedResidentGpuBytes: this.peakEstimatedResidentGpuBytes,
      peakEstimatedResidentTotalBytes: this.peakEstimatedResidentTotalBytes,
      peakProtectedEntries: this.peakProtectedEntries,
      lastTrim: { ...this.lastTrim },
    }
  }

  /** 释放全部已接入资产载荷并清空管理状态。 */
  dispose(): void {
    for (const assetId of Array.from(this.assetGeometries.keys())) {
      this.releaseAssetPayload(assetId)
      this.detachAsset(assetId)
    }
    this.protectedNodes.clear()
    this.protectedEntries = 0
    this.protectedResidentPoints = 0
    this.residentDescendantCounts.clear()
    this.lru.clear()
  }

  private syncNode(node: OctreeGeometryNode): void {
    const assetId = this.geometryAssets.get(node.octreeGeometry)
    const state = node.resourceState
    if (assetId === undefined || (state !== 'decoded' && state !== 'resident')) {
      const entry = this.entries.get(node)
      if (entry) {
        this.removeEntry(node, entry)
      }
      return
    }

    const metrics = node.getResourceMetrics()
    const previous = this.entries.get(node)
    const isProtected = this.protectedNodes.has(node)
    if (previous) {
      this.subtractEntry(previous)
      if (isProtected) {
        this.protectedResidentPoints -= previous.points
      }
    } else {
      this.lru.add(node)
      this.adjustResidentAncestorCounts(node, 1)
    }
    const entry: ResidentEntry = { ...metrics, assetId, state }
    this.entries.set(node, entry)
    this.addEntry(entry)
    if (isProtected) {
      this.protectedResidentPoints += entry.points
      if (!previous) {
        this.protectedEntries += 1
        this.updateProtectedPeak()
      }
    }
  }

  private removeEntry(node: OctreeGeometryNode, entry: ResidentEntry): void {
    this.subtractEntry(entry)
    this.entries.delete(node)
    this.lru.remove(node)
    this.adjustResidentAncestorCounts(node, -1)
    if (this.protectedNodes.has(node)) {
      this.protectedEntries = Math.max(0, this.protectedEntries - 1)
      this.protectedResidentPoints = Math.max(0, this.protectedResidentPoints - entry.points)
    }
  }

  private addEntry(entry: ResidentEntry): void {
    this.residentPoints += entry.points
    this.estimatedResidentCpuBytes += entry.estimatedCpuBytes
    this.estimatedResidentGpuBytes += entry.estimatedGpuBytes
    this.estimatedResidentTotalBytes += entry.estimatedTotalBytes
    this.updateResourcePeaks()
  }

  private subtractEntry(entry: ResidentEntry): void {
    this.residentPoints -= entry.points
    this.estimatedResidentCpuBytes -= entry.estimatedCpuBytes
    this.estimatedResidentGpuBytes -= entry.estimatedGpuBytes
    this.estimatedResidentTotalBytes -= entry.estimatedTotalBytes
  }

  private getTrimTrigger(): PointCloudTrimTrigger {
    const pointsExceeded =
      this.getCachedResidentPoints() > this.cachedPointHighWatermark ||
      (this.pointHighWatermark !== null && this.residentPoints > this.pointHighWatermark)
    const estimatedBytesExceeded =
      this.estimatedByteHighWatermark !== null &&
      this.estimatedResidentTotalBytes > this.estimatedByteHighWatermark
    if (pointsExceeded && estimatedBytesExceeded) {
      return 'both'
    }
    if (pointsExceeded) {
      return 'points'
    }
    return estimatedBytesExceeded ? 'estimatedBytes' : 'none'
  }

  private isAboveLowWatermarks(): boolean {
    if (this.getCachedResidentPoints() > this.cachedPointLowWatermark) {
      return true
    }
    if (this.pointLowWatermark !== null && this.residentPoints > this.pointLowWatermark) {
      return true
    }
    return (
      this.estimatedByteLowWatermark !== null &&
      this.estimatedResidentTotalBytes > this.estimatedByteLowWatermark
    )
  }

  private enqueueSafeCandidate(
    node: OctreeGeometryNode,
    ranks: ReadonlyMap<OctreeGeometryNode, number>,
    candidates: BinaryHeap<RankedCandidate>,
    queuedNodes: Set<OctreeGeometryNode>,
    blockedNodes: Set<OctreeGeometryNode>
  ): void {
    if (!this.entries.has(node) || queuedNodes.has(node)) {
      return
    }
    if (this.protectedNodes.has(node)) {
      blockedNodes.add(node)
      return
    }
    if ((this.residentDescendantCounts.get(node) ?? 0) > 0) {
      return
    }
    const rank = ranks.get(node)
    if (rank === undefined) {
      return
    }
    queuedNodes.add(node)
    candidates.push({ node, rank })
  }

  private popSafeCandidate(
    candidates: BinaryHeap<RankedCandidate>,
    queuedNodes: Set<OctreeGeometryNode>,
    blockedNodes: Set<OctreeGeometryNode>
  ): OctreeGeometryNode | null {
    for (let candidate = candidates.pop(); candidate; candidate = candidates.pop()) {
      const { node } = candidate
      queuedNodes.delete(node)
      if (!this.entries.has(node)) {
        continue
      }
      if (this.protectedNodes.has(node)) {
        blockedNodes.add(node)
        continue
      }
      if ((this.residentDescendantCounts.get(node) ?? 0) === 0) {
        return node
      }
    }
    return null
  }

  private enqueueSafeAncestors(
    node: OctreeGeometryNode | null | undefined,
    ranks: ReadonlyMap<OctreeGeometryNode, number>,
    candidates: BinaryHeap<RankedCandidate>,
    queuedNodes: Set<OctreeGeometryNode>,
    blockedNodes: Set<OctreeGeometryNode>
  ): void {
    let current = node
    while (current) {
      this.enqueueSafeCandidate(current, ranks, candidates, queuedNodes, blockedNodes)
      current = current.parent
    }
  }

  private adjustResidentAncestorCounts(node: OctreeGeometryNode, delta: 1 | -1): void {
    let current = node.parent
    while (current) {
      const count = (this.residentDescendantCounts.get(current) ?? 0) + delta
      if (count > 0) {
        this.residentDescendantCounts.set(current, count)
      } else {
        this.residentDescendantCounts.delete(current)
      }
      current = current.parent
    }
  }

  private forEachTopologyNode(
    root: OctreeGeometryNode,
    visit: (node: OctreeGeometryNode) => void
  ): void {
    const stack = [root]
    while (stack.length > 0) {
      const node = stack.pop()!
      visit(node)
      if (typeof node.getChildren === 'function') {
        stack.push(...node.getChildren())
      }
    }
  }

  private accumulateRelease(summary: PointCloudTrimSummary, release: NodeReleaseRecord): void {
    summary.releasedPoints += release.points
    summary.releasedEstimatedCpuBytes += release.estimatedCpuBytes
    summary.releasedEstimatedGpuBytes += release.estimatedGpuBytes
    summary.releasedEstimatedTotalBytes += release.estimatedTotalBytes
  }

  private recountProtectedEntries(): void {
    let count = 0
    let points = 0
    for (const node of this.protectedNodes) {
      const entry = this.entries.get(node)
      if (entry) {
        count += 1
        points += entry.points
      }
    }
    this.protectedEntries = count
    this.protectedResidentPoints = points
    this.updateProtectedPeak()
  }

  private getCachedResidentPoints(): number {
    return Math.max(0, this.residentPoints - this.protectedResidentPoints)
  }

  private updateProtectedPeak(): void {
    this.peakProtectedEntries = Math.max(this.peakProtectedEntries, this.protectedEntries)
  }

  private updateResourcePeaks(): void {
    this.peakResidentEntries = Math.max(this.peakResidentEntries, this.entries.size)
    this.peakResidentPoints = Math.max(this.peakResidentPoints, this.residentPoints)
    this.peakEstimatedResidentCpuBytes = Math.max(
      this.peakEstimatedResidentCpuBytes,
      this.estimatedResidentCpuBytes
    )
    this.peakEstimatedResidentGpuBytes = Math.max(
      this.peakEstimatedResidentGpuBytes,
      this.estimatedResidentGpuBytes
    )
    this.peakEstimatedResidentTotalBytes = Math.max(
      this.peakEstimatedResidentTotalBytes,
      this.estimatedResidentTotalBytes
    )
  }
}
